import { createApi } from '@reduxjs/toolkit/query/react'
import { ARCHIVE_CASELOAD, ASSIGN_MULTIPLE_THERAPISTS, ASSIGN_THERAPIST, BULK_REMOVE_THERAPIST_CASELOAD, CLINICIAN_HISTORY_LIST, CREATE_NEW_CASELOAD, LIST_CLIENT_CASELOADS, REMOVE_THERAPIST_ASSIGNMENT, REMOVE_THERAPIST_CASELOAD, RESET_MDT_STATUS, UPDATE_CASELOAD_STATUS, UPDATE_MDT_STATUS, UPDATE_THERAPIST_ASSIGNMENT, UPDATE_THERAPIST_SERVICE_STATUS } from "config/apiPaths"
import axiosBaseQuery from "utils/axios/axiosBaseQuery"
import { invalidateAPITags } from "utils/common";
import { mdtAssessmentApi } from "./MDT/assessment";
import { invalidateClientList } from "./clients/invalidateClientList";

export const clientCaseLoadsApi = createApi({
  reducerPath: 'clientCaseLoadsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: "/client/caseload",
  }),
  tagTypes: ["SchoolClientDetails"],
  endpoints: (builder) => ({
    assignTherapist: builder.mutation({
      query: ({ caseload_id, therapist_id, discipline_id, data = {} }) => ({
        method: "PATCH",
        url: `${ASSIGN_THERAPIST}/${caseload_id}/${therapist_id}/${discipline_id}`,
        data
      }),
      invalidatesTags: ["SchoolClientDetails", "cliniciansHistory"]
    }),
    assignMultipleTherapists: builder.mutation({
      query: ({ data = {} }) => ({
        method: "POST",
        url: `${ASSIGN_MULTIPLE_THERAPISTS}`,
        data
      }),
      invalidatesTags: ["SchoolClientDetails", "cliniciansHistory"]
    }),
    removeTherapistAssignment: builder.mutation({
      query: ({ caseLoadId, mappingId, therapistId }) => ({
        method: "DELETE",
        url: `${REMOVE_THERAPIST_ASSIGNMENT}/${caseLoadId}/${mappingId}/${therapistId}`
      }),
      invalidatesTags: ["SchoolClientDetails"]
    }),
    updateTherapistAssignment: builder.mutation({
      query: ({ payload, assignmentId }) => ({
        method: "PATCH",
        url: `${UPDATE_THERAPIST_ASSIGNMENT}/${assignmentId}`,
        data: payload
      }),
    }),
    updateMdtStatus: builder.mutation({
      query: ({ id, type }) => ({
        method: "PATCH",
        url: `${UPDATE_MDT_STATUS}/${type}/${id}`
      }),
      invalidatesTags: ["SchoolClientDetails", "SchoolClients"]
    }),

    resetMdtStatus: builder.mutation({
      query: (id) => ({
        method: "PATCH",
        url: `${RESET_MDT_STATUS}/${id}`
      })
    }),
    changeTherapist: builder.mutation({
      query: ({ assignmentId, therapistId, caseloadMappingId }) => ({
        method: "DELETE",
        url: `${REMOVE_THERAPIST_ASSIGNMENT}/${assignmentId}/${caseloadMappingId}/${therapistId}`
      }),
      invalidatesTags: ["SchoolClientDetails"]
    }),
    removeTherapist: builder.mutation({
      query: (assignmentId) => ({
        method: "DELETE",
        url: `${REMOVE_THERAPIST_CASELOAD}/${assignmentId}`
      }),
      invalidatesTags: ["SchoolClientDetails", "cliniciansHistory"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        invalidateAPITags(queryFulfilled, dispatch, mdtAssessmentApi, ["mdtAssessmentList", "mdtAssessmentDetails"]);
      },
    }),
    // Bulk unassign from the clinician detail page client tabs.
    // clientType only selects which list cache to drop - the endpoint rejects unknown body keys.
    bulkRemoveTherapistCaseload: builder.mutation({
      query: ({ therapist_id, client_ids }) => ({
        method: "PATCH",
        url: BULK_REMOVE_THERAPIST_CASELOAD,
        data: { therapist_id, client_ids }
      }),
      invalidatesTags: ["SchoolClientDetails"],
      onQueryStarted: async ({ clientType }, { dispatch, queryFulfilled }) => {
        invalidateClientList(queryFulfilled, dispatch, clientType);
      },
    }),
    archiveCaseload: builder.mutation({
      query: (caseloadId) => ({
        method: "DELETE",
        url: `${ARCHIVE_CASELOAD}/${caseloadId}`
      }),
      invalidatesTags: ["SchoolClientDetails"]
    }),
    updateTherapistServiceStatus: builder.mutation({
      query: ({ payload, id }) => ({
        method: "PATCH",
        url: `${UPDATE_THERAPIST_SERVICE_STATUS}/${id}`,
        data: payload
      }),
    }),
    updateCaseloadStatus: builder.mutation({
      query: ({ payload, id }) => ({
        method: "PATCH",
        url: `${UPDATE_CASELOAD_STATUS}/${id}`,
        data: payload
      }),
      invalidatesTags: ['clientCaseloadsList']
    }),
    createNewCaseload: builder.mutation({
      query: ({ payload, id }) => ({
        method: "POST",
        url: `${CREATE_NEW_CASELOAD}/${id}`,
        // data: payload
      }),
      invalidatesTags: ['clientCaseloadsList']
    }),
    getClientCaseloadsList: builder.query({
      query: ({ id, search = "", status = "", page = 1, limit = 10, sort = { column: "id", direction: "DESC" } }) => ({
        method: "PATCH",
        url: `${LIST_CLIENT_CASELOADS}/${id}`,
        data: { search, status, page, limit, sort }
      }),

      transformResponse: (response) => {
        return response.data;
      },
      providesTags: ["clientCaseloadsList"]
    }),
    getCliniciansHistory: builder.query({
      query: ({ clientId }) => ({
        method: "PATCH",
        url: `${CLINICIAN_HISTORY_LIST}/${clientId}`,
      }),

      transformResponse: (response) => {
        return response.data;
      },
      providesTags: ["cliniciansHistory"]
    }),
  }),
})

export const {
  useAssignTherapistMutation,
  useRemoveTherapistAssignmentMutation,
  useUpdateTherapistAssignmentMutation,
  useUpdateMdtStatusMutation,
  useResetMdtStatusMutation,
  useChangeTherapistMutation,
  useRemoveTherapistMutation,
  useArchiveCaseloadMutation,
  useUpdateTherapistServiceStatusMutation,
  useUpdateCaseloadStatusMutation,
  useCreateNewCaseloadMutation,
  useGetClientCaseloadsListQuery,
  useAssignMultipleTherapistsMutation,
  useGetCliniciansHistoryQuery,
  useBulkRemoveTherapistCaseloadMutation
} = clientCaseLoadsApi;